import { SettingsNavigation } from "./layout/settings-navigation";
import { OpenConnections } from "@/shared/components/models/OpenConnections";
import { AgentAvatarState } from "@/shared/avatar/BuddyAvatar";
import { useOverview } from "@/shared/api/overview";
import { fastSelectionOf, selectFastModel, selectionOf, toModelChoice } from "@/shared/lib/models";
import React, { useEffect, useMemo, useRef, useState } from "react";
import "@/app.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import LeftSidebar from "./layout/LeftSidebar";
import { WORKSPACE_VIEWS } from "./layout/WorkspaceNav";
import { BUILDER, paneOf, pathOf, placeOf, type Pane, type Place } from "./places";
import OldHashLinks from "./OldHashLinks";
import TopBar from "./layout/TopBar";
import { RequireAuth, LoginPage, SignupPage, AuthCallbackPage } from "@/features/auth";
import { ChatPane, ArchivePage, ChatHistory, SharedChatPage, type BuilderStart } from "@/features/chat";
import { ExtensionLinkPage } from "@/features/extension-link";
import { ChatKnowledge, ConfigSummary, OverviewPage, AgentSettingsPage, McpOAuthCallback, UnpublishedNote } from "@/features/agents";
import { Onboarding, CreateOrg } from "@/features/home";
import { AppsPage } from "@/features/apps";
import { SkillsPage } from "@/features/skills";
import { TemplatesPage } from "@/features/templates";
import { KnowledgeView } from "@/features/knowledge";
import { SpacesPage, type SpacesPlace } from "@/features/spaces";
import { TasksPage, SchedulePage, browserZone, toAgentTask, toSchedule, toTaskPatch } from "@/features/automations";
import { InterfacesPage } from "@/features/interfaces";
import { OnboardingSection } from "@/features/onboarding";
import { ReportsPage } from "@/features/reports";
import { catalogFor } from "@/shared/lib/interface-catalog";
import { OPEN_TASKS } from "@/shared/lib/openTasks";
import { OPEN_WORKSPACE_SETTINGS } from "@/shared/lib/openWorkspaceSettings";
import { TracesPage, EvalsPage } from "@/features/observability";
import { useSettings } from "@/shared/api/settings";
import {
  agentPatch,
  capabilityPatch,
  mergeAgent,
  toAgent,
  useAgents,
  useCreateAgent,
  useDeleteAgent,
  usePatchAgent,
} from "@/shared/api/agents";
import { ApiError } from "@/shared/api/errors";
import { toDefinable, useBuilder } from "@/shared/api/builder";
import {
  toChat,
  useArchivedThreads,
  useBranchThread,
  useDeleteThread,
  useThread,
  useThreads,
  useUpdateThread,
} from "@/shared/api/threads";
import { useCreateTask, useDeleteTask, usePatchTask, useRunTask, useTasks } from "@/shared/api/tasks";
import { loadUserAvatar } from "@/shared/avatar/userAvatar";
import { draftsFor } from "@/shared/lib/drafts";
import { signOut } from "@/features/auth";
import { SettingsPage, useCheckoutReturn } from "@/features/settings";
import type { SettingsSectionId } from "@/features/settings";
import { Toaster } from "@/shared/components/ui/sonner";
import { toast } from "sonner";
import { useViewport } from "@/shared/hooks/useViewport";
import { useEvents } from "@/shared/api/events";
import SideSheet from "@/shared/components/responsive/SideSheet";
import { Agentation } from "agentation";

import type {
  Agent, AgentReport, AgentTask, Interface, NavigationOptions, Template, View,
} from "@/shared/types";

/** Wraps a navigation callback without changing what it takes — the sheet
    placement uses it to close itself on selection, the inline one is identity. */
type NavWrap = <A extends unknown[]>(fn: (...args: A) => void) => (...args: A) => void;

function Workspace() {
  /* THE ADDRESS IS THE PAGE (app/places.ts). What is open (the page, the agent, the chat, a
     section, a base, a table, a run) is read from the path, and every way somewhere is a
     `navigate`: Back and Forward, a reload and a pasted link land where the address says. */
  const location = useLocation();
  const navigate = useNavigate();
  const place = useMemo(() => placeOf(location.pathname), [location.pathname]);
  /* THE WORKSPACE IS THE SERVER'S. It starts empty and fills from `GET /agents`
     — nothing is shown in its place while the list loads, and an org with no
     agents has none on screen.
     Only the fields the API owns are read back (`mergeAgent`), so a refetch
     keeps what the shell laid over the agent. */
  const [storedAgents, setAgents] = useState<Agent[]>([]);
  const { data: agentRows, isPending: agentsPending } = useAgents();
  const { data: activity } = useOverview(1);
  const avatarStates = useMemo(() => {
    const running = new Map(activity?.agents.map((agent) => [agent.agent_id, agent.running]));
    return new Map((agentRows ?? []).map((agent) => [agent.seed, { running: running.get(agent.id), unpublished: agent.has_unpublished_changes }]));
  }, [activity, agentRows]);
  const createAgentApi = useCreateAgent();
  const patchAgentApi = usePatchAgent();
  const deleteAgentApi = useDeleteAgent();

  useEffect(() => {
    if (!agentRows) return;
    setAgents((prev) =>
      agentRows.map((row) => {
        const held = prev.find((a) => a.id === row.id);
        return held ? mergeAgent(held, row) : toAgent(row);
      }),
    );
  }, [agentRows]);
  /* Definable, the workspace's own agent: never in the agents list, opened from its own
     sidebar row, at `/builder`. */
  const { data: builderRow, isPending: builderPending } = useBuilder();
  // The agent an agent page is about; none on a workspace page.
  const activeAgentId = place.agentId === BUILDER ? builderRow?.agent_id ?? "" : place.agentId ?? "";
  /* An agent's chats are its threads, read from the API and never held
     here: the stored agent keeps `chats: []` and the list is laid over it.
     Only the active agent's threads are fetched — the one you are looking at —
     plus every agent's archived ones, for the Archive page. */
  const { data: threads } = useThreads(activeAgentId || null);
  const { data: archivedThreads } = useArchivedThreads(storedAgents.length > 0);
  /* An agent's tasks are the API's, laid over it the same way: the Tasks
     page and the Schedule calendar both read `agent.tasks`. */
  const { data: taskRows } = useTasks(storedAgents.length > 0);
  const agents = useMemo(
    () =>
      storedAgents.map((a) => {
        const listed = a.id === activeAgentId ? (threads ?? []) : [];
        const archived = (archivedThreads ?? []).filter((t) => t.agent_id === a.id);
        const tasks = (taskRows ?? []).filter((t) => t.agent_id === a.id).map(toAgentTask);
        return { ...a, chats: [...listed, ...archived].map(toChat), tasks };
      }),
    [storedAgents, threads, archivedThreads, taskRows, activeAgentId],
  );
  // Definable's chats are its threads like any agent's (yours only, the server says).
  const definable = useMemo(
    () => builderRow && { ...toDefinable(builderRow), chats: builderRow.agent_id === activeAgentId ? (threads ?? []).map(toChat) : [] },
    [builderRow, threads, activeAgentId],
  );
  /** Every agent a chat action may name: yours and Definable. */
  const anyAgent = (id: string) => (definable?.id === id ? definable : agents.find((a) => a.id === id));
  const createTask = useCreateTask();
  const patchTask = usePatchTask();
  const deleteTaskApi = useDeleteTask();
  const runTaskApi = useRunTask();
  const updateThread = useUpdateThread();
  const branchThread = useBranchThread();
  const deleteThread = useDeleteThread();
  // The rest of the place, as each page takes it; the pages correct what they can't open.
  const settingsSection = (place.view === "settings" && place.section) || undefined;
  const workspaceSection = (place.view === "workspace-settings" && (place.section as SettingsSectionId)) || "general";
  const activeKbId = place.kbId ?? null;
  const activeKbNodeId = place.nodeId ?? null;
  const spacesPlace = useMemo<SpacesPlace>(() => ({ tableId: place.tableId ?? null, rowId: place.rowId ?? null }), [place]);
  // A run Traces opens at: "Source trace" on an eval case lands on the run it was captured from.
  const traceRunId = place.runId ?? null;
  // Always opens expanded; collapse is a per-session choice, not persisted.
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  /* ---- responsive shell (mobile/tablet) ----
     Below lg the sidebars leave the flow and live in a left SideSheet
     opened from the top-bar hamburger. Desktop layout is untouched. */
  const { isDesktop } = useViewport();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // New chats started in this tab: each is a pane of its own (`pane` below). Counted from the
  // load's time, so a token a history entry kept across a reload is never handed out again.
  const newChatCount = useRef(Date.now());
  const [desktopChatsOpen, setDesktopChatsOpen] = useState(true);
  const [mobileChatsOpen, setMobileChatsOpen] = useState(false);
  const chatsOpen = isDesktop ? desktopChatsOpen : mobileChatsOpen;
  const setChatsOpen = isDesktop ? setDesktopChatsOpen : setMobileChatsOpen;
  // Wrap a navigation callback so picking a destination closes the sheet.
  const viaSheet: NavWrap = (fn) => (...args) => { setMobileNavOpen(false); fn(...args); };
  // New-agent dialog for sheet mode — hoisted here because a dialog nested
  // inside the sheet unmounts when the sheet dismisses.

  /* THE SELECTION MAY NOT OUTLIVE THE LIST. Most screens below are handed
     `activeAgent!`, so an id pointing at nobody is not an empty state — it is
     a screen reading `agent.instructions` off undefined and taking the whole
     app down with it. The list changes underneath the selection
     whenever it first loads (a deep link can name an agent that is gone) or the
     active agent is deleted (Back to its page, after).
     So an agent that is gone shows Overview, decided in render, not in an effect: an
     effect corrects one frame too late, after the crash. Never another agent's page,
     whose buttons would act on the wrong agent. */
  const definableOpen = !!definable && activeAgentId === definable.id;
  const activeAgent = definableOpen ? definable : agents.find((a) => a.id === activeAgentId);
  const activeChatId = (place.view === "chat" && activeAgent && place.chatId) || undefined;
  const requestedView = place.view;
  // Agent views need an agent: an org with none sees Overview, whatever was asked for.
  // Definable has only its Agent tab; any other agent view asks for its chat.
  // Workspace pages belong to no agent, so they open from Definable as from any agent.
  const view: View = definableOpen && !["chat", ...WORKSPACE_VIEWS].includes(requestedView) ? "chat"
    : activeAgent || WORKSPACE_VIEWS.includes(requestedView) ? requestedView : "overview";
  // Workspace pages open full height; agent pages keep their tab strip above the card.
  const workspaceView = WORKSPACE_VIEWS.includes(view);
  /* The chat pane on screen, from the history entry so Back and Forward bring the same one: each
     New chat is a pane of its own and keeps it when its first message names the chat; any chat
     opened is the "open" pane, so switching chats keeps the pane mounted. */
  const pane: Pane = paneOf(location.state) ?? (activeChatId ? "open" : "new");

  /** Goes to a place: a new history entry, or in place of this one when `replace` says so or the
      path is where we are already, which also keeps its pane (nothing remounts). `pane`: the
      chat pane the entry opens. */
  const go = (to: Place, how: NavigationOptions & { pane?: Pane } = {}) => {
    const path = pathOf(to, builderRow?.agent_id);
    const samePath = path === window.location.pathname;
    navigate(path, { replace: how.replace ?? samePath, state: how.pane ? { pane: how.pane } : samePath ? location.state : null });
  };

  // ...and the address follows what is shown, in place: an agent that is gone, a page Definable
  // doesn't have, Definable named by its id, an address that names nothing. Not before the list
  // answers and the shell holds it (until then a deep-linked agent is in no list), nor before
  // Definable answers.
  const listHeld = !!agentRows && storedAgents.length === agentRows.length;
  useEffect(() => {
    if (agentsPending || builderPending || !listHeld) return;
    const moved = view !== place.view || (!workspaceView && activeAgent?.id !== activeAgentId);
    const shown: Place = !moved ? { ...place, agentId: activeAgentId || undefined }
      : workspaceView ? { view } : { view, agentId: activeAgent?.id };
    const path = pathOf(shown, builderRow?.agent_id);
    if (path !== location.pathname) navigate(path + location.search, { replace: true, state: location.state });
  }, [agentsPending, builderPending, listHeld, view, place, workspaceView, activeAgent, activeAgentId, builderRow, location, navigate]);

  /* Where each agent's pages were last seen in this tab, so its sidebar row and tabs come back
     there: the chat (none for a New chat), the Spaces table and row, and Settings' section (kept
     across agents). Written on every visit; read only by the ways in. */
  const lastSeen = useRef<{
    chat: Record<string, { chatId?: string; pane: Pane } | undefined>;
    spaces: Record<string, SpacesPlace>;
    section?: string;
  }>({ chat: {}, spaces: {} });
  // Where the person is now, for an answer that comes back after they moved on (`rememberChat`).
  const whereNow = useRef({ view, agentId: activeAgentId, chatId: activeChatId, pane });
  useEffect(() => {
    whereNow.current = { view, agentId: activeAgentId, chatId: activeChatId, pane };
    if (!activeAgentId || workspaceView) return;
    if (view === "chat") lastSeen.current.chat[activeAgentId] = { chatId: activeChatId, pane };
    if (view === "spaces") lastSeen.current.spaces[activeAgentId] = spacesPlace;
    if (view === "settings" && settingsSection) lastSeen.current.section = settingsSection;
  });

  const listedChat = activeAgent?.chats.find((c) => c.id === activeChatId);
  /* "Open chat" and "View chats" can open a thread the list does not hold yet
     (the list is only the latest page), so that one is read on its own. */
  const { data: openedThread, error: openedError } = useThread(!listedChat && activeAgentId ? activeChatId ?? null : null);
  const activeChat = listedChat ?? (openedThread && toChat(openedThread));
  // A chat the address names that can't be read (deleted, Back to it after) is corrected to the
  // agent's New chat, in place: a message sent from it would find no chat to land in.
  const chatGone = !!activeChatId && !listedChat && openedError instanceof ApiError && openedError.status === 404;
  useEffect(() => {
    if (chatGone) go({ view: "chat", agentId: activeAgentId }, { replace: true });
  }, [chatGone]); // eslint-disable-line react-hooks/exhaustive-deps -- once per gone chat; go reads where we are

  // Every way into workspace Settings lands here: the profile pill,
  // Upgrade, "Add connection", a checkout return. On a phone it also closes the sheet.
  const openSettings = (section: SettingsSectionId) => {
    setMobileNavOpen(false);
    go({ view: "workspace-settings", section });
  };
  useEffect(() => {
    const open = () => openSettings("billing");
    window.addEventListener("open-billing", open);
    return () => window.removeEventListener("open-billing", open);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- openSettings only navigates, by absolute path
  useEffect(() => {
    const open = (e: Event) => openSettings((e as CustomEvent<SettingsSectionId>).detail);
    window.addEventListener(OPEN_WORKSPACE_SETTINGS, open);
    return () => window.removeEventListener(OPEN_WORKSPACE_SETTINGS, open);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- openSettings only navigates, by absolute path
  // After the listener above: it opens Billing for the payer the provider sent back.
  useCheckoutReturn();

  // Small screens: collapse the rail and give the chat column room.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1100px)");
    const apply = () => {
      if (mq.matches) setLeftCollapsed(true);
    };
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const setActiveChat = (agentId: string, chatId: string) => {
    go({ view: "chat", agentId, chatId });
    setMobileChatsOpen(false);
  };

  // An agent's chat as it was left: the chat last open with it, or the New chat it was on (the same
  // pane, so a first message's answer still finds it).
  const openChat = (agentId: string) => {
    const seen = lastSeen.current.chat[agentId];
    go({ view: "chat", agentId, chatId: seen?.chatId }, seen && !seen.chatId ? { pane: seen.pane } : {});
  };

  /* A first message started this chat on the server. Still on that New chat: the address takes
     the chat's id, in place. Gone to another page: the agent's row comes back to the chat, unless
     another chat with it was open since. On another chat with the agent, or a newer New chat:
     nothing moves. The answer can come back after any of these. */
  const rememberChat = (agentId: string, chatId: string, fromPane: Pane) => {
    const now = whereNow.current;
    if (now.view === "chat" && now.agentId === agentId) {
      if (!now.chatId && now.pane === fromPane) go({ view: "chat", agentId, chatId }, { replace: true, pane: fromPane });
      return;
    }
    const seen = lastSeen.current.chat[agentId];
    if (!seen || (!seen.chatId && seen.pane === fromPane)) lastSeen.current.chat[agentId] = { chatId, pane: fromPane };
  };

  // `from`: "View chats" on an interface lists only the conversations that came through it.
  const [threadsFrom, setThreadsFrom] = useState<Interface | null>(null);
  const openThreads = (agentId: string, from: Interface | null = null) => {
    openChat(agentId);
    setThreadsFrom(from);
    setChatsOpen(true);
  };

  // Selecting an agent (or Definable) comes back to the chat last open with it, as you left it;
  // New chat is the way to a fresh one.
  const selectAgent = (agentId: string) => {
    openChat(agentId);
    setThreadsFrom(null);
    setMobileChatsOpen(false);
  };

  // A workspace page (Knowledge on its home), or one of this agent's tabs.
  const openView = (v: View) => go(WORKSPACE_VIEWS.includes(v) ? { view: v } : { view: v, agentId: activeAgentId });
  // A base and a folder or file in it; KnowledgeView corrects a gone base in place (`replace`).
  const openKb = (kbId: string | null, nodeId: string | null = null, how?: NavigationOptions) =>
    go({ view: "knowledge", kbId, nodeId }, how);

  const openTasks = (agentId: string) => go({ view: "tasks", agentId });
  useEffect(() => {
    const open = (e: Event) => {
      setMobileNavOpen(false);
      openTasks((e as CustomEvent<string>).detail);
    };
    window.addEventListener(OPEN_TASKS, open);
    return () => window.removeEventListener(OPEN_TASKS, open);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- openTasks only navigates, by absolute path

  /* Task writes go to the API and resolve when it answered (a refusal rejects,
     and the global toast says why). */
  const addTask = async (agentId: string, task: AgentTask) => {
    await createTask.mutateAsync({
      agent_id: agentId,
      name: task.name,
      prompt: task.prompt,
      // a duplicate keeps its source's zone; a new task takes the browser's
      schedule: toSchedule(task.spec!, task.timezone ?? browserZone()),
      paused: !!task.paused,
    });
  };

  const updateTask = async (agentId: string, taskId: string, patch: Partial<AgentTask>) => {
    const zone = agents.find((a) => a.id === agentId)?.tasks?.find((t) => t.id === taskId)?.timezone;
    await patchTask.mutateAsync({ id: taskId, body: toTaskPatch(patch, zone ?? browserZone()) });
  };

  const deleteTask = async (_agentId: string, taskId: string) => {
    await deleteTaskApi.mutateAsync(taskId);
  };

  const openInterfaces = (agentId: string) => go({ view: "interfaces", agentId });


  const openReports = (agentId: string) => go({ view: "reports", agentId });

  // An agent's Spaces opens on the table and row left open in it; a table id is one agent's.
  const openSpaces = (agentId: string) => {
    go({ view: "spaces", agentId, ...lastSeen.current.spaces[agentId] });
  };

  // How the next new Builder chat opens (`BuilderStart`): "Fix with Builder" on a report sends
  // the report as its first message, with the agent picked, so the fix starts in the person's
  // own Builder conversation.
  const [builderStart, setBuilderStart] = useState<BuilderStart | null>(null);
  const fixWithBuilder = (agentId: string, report: AgentReport) => {
    if (!definable) return;
    const agentName = anyAgent(agentId)?.name ?? "the agent";
    setBuilderStart({
      send: `Fix this report on ${agentName} (agent_id ${agentId}, report ${report.id}, run ${report.run_id}): ` +
        `"${report.summary}"${report.needed ? ` It needed: ${report.needed}` : ""}`,
      pick: { kind: "agent", agent: { id: agentId, name: agentName } },
    });
    createChat(definable.id);
  };

  // The composer's + → New agent: a new Builder chat set to New agent, the draft left in its box
  const newAgentWithBuilder = (draft: string) => {
    if (!definable) return;
    setBuilderStart({ draft, pick: { kind: "new" } });
    createChat(definable.id);
  };

  // The Interfaces page sent a test message; it lands in the sender's test chat,
  // where the agent answers it — unless the interface is paused.
  const simulateIncoming = (iface: Interface, threadId: string) => {
    toast.success(`Test message sent through ${catalogFor(iface.type)?.name ?? "the interface"}`, {
      description: iface.status === "paused" ? "The interface is paused, so nobody answers it." : "The agent answers it in your test chat.",
      action: { label: "Open chat", onClick: () => setActiveChat(iface.agent_id, threadId) },
    });
  };

  // An agent's Settings opens on the section last open in any agent's.
  const openAgentSettings = (agentId: string, section = lastSeen.current.section) => go({ view: "settings", agentId, section });

  // A skill's agents open on the Skills section of their Settings
  const openAgentSkills = (agentId: string) => openAgentSettings(agentId, "skills");

  // "Ask Definable" on the Skills page: its chat opens with the ask as the first message
  const askDefinable = (prompt: string) => {
    if (!definable) return;
    setBuilderStart({ send: prompt });
    createChat(definable.id);
  };

  const openTraces = (agentId: string, runId: string | null = null) => go({ view: "traces", agentId, runId });

  const openEvals = (agentId: string) => go({ view: "evals", agentId });

  // Fresh copy of an agent's definition — chats and files stay behind.
  const duplicateAgent = async (agentId: string) => {
    const src = agents.find((a) => a.id === agentId);
    if (!src) return;
    // Same model on the same key.
    const row = await createAgentApi
      .mutateAsync({ name: `${src.name} copy`, instructions: src.instructions || null, max_tokens: src.modelSettings.maxTokens, ...toModelChoice(selectionOf(src)) })
      .catch(() => null);
    if (!row) return;
    // The capabilities are a second write: the create body has no room for them, and a
    // duplicate that arrived with its apps switched off is not one.
    const created = toAgent(row);
    const fastSelection = fastSelectionOf(src);
    const withFast = selectFastModel(created, fastSelection);
    const copy = { ...withFast, capabilities: { ...src.capabilities } };
    patchAgentApi.mutate({ id: row.id, body: { capabilities: capabilityPatch(src.capabilities) } });
    // The fast model is a third write, so a fast key that broke since costs the copy only that.
    if (fastSelection) patchAgentApi.mutate({ id: row.id, body: agentPatch(created, withFast) });
    setAgents((prev) => [copy, ...prev]);
    go({ view: "settings", agentId: copy.id });
    toast.success(`${copy.name} created`, { description: "Same setup — chats stayed with the original." });
  };

  const deleteAgent = (agentId: string) => {
    const a = agents.find((x) => x.id === agentId);
    if (a) deleteAgentApi.mutate(a.id);
    setAgents((prev) => prev.filter((x) => x.id !== agentId));
    go({ view: "overview" });
    toast.success(`${a?.name || "Agent"} deleted`);
  };

  /* Every edit in an agent's Settings — a capability switch, the instructions —
     comes through here, so this is where the change is sent. `agentPatch` is `{}`
     for fields with no column, and a `{}` patch is not a request. */
  const updateAgent = (agentId: string, updater: (a: Agent) => Agent) => {
    // The diff is taken here and not inside the updater below: a state updater
    // can be replayed (StrictMode does), and a replayed PATCH is a second write.
    const before = agents.find((a) => a.id === agentId);
    if (before) {
      const body = agentPatch(before, updater(before));
      if (Object.keys(body).length) patchAgentApi.mutate({ id: agentId, body });
    }
    setAgents((prev) => prev.map((a) => (a.id === agentId ? updater(a) : a)));
  };

  // Template → real agent in "Your agents".
  /* A template is a name and a system prompt the API can hold; its tasks,
     interfaces and knowledge folders have no routes yet, and an agent that
     arrived carrying them would be promising work nothing runs. They come back
     when those domains have routes of their own. */
  const useTemplate = async (t: Template) => {
    const row = await createAgentApi
      .mutateAsync({ name: t.name || "New agent", instructions: t.instructions || null })
      .catch(() => null);
    if (!row) return false;
    const agent = toAgent(row);
    setAgents((prev) => [agent, ...prev]);
    go({ view: "chat", agentId: agent.id });
    return true;
  };

  // A task run is real: it starts at once and answers in a new chat.
  const runTask = (agentId: string, task: AgentTask) => {
    runTaskApi.mutate(task.id, {
      onSuccess: (started) =>
        toast.success(`「${task.name}」 is running`, {
          description: "It answers in a new chat.",
          action: { label: "Open chat", onClick: () => setActiveChat(agentId, started.thread_id) },
        }),
    });
  };

  // A thread is created by its first message (`POST /runs`), so a new chat is
  // just no chat open until then.
  const createChat = (agentId: string) => {
    newChatCount.current += 1;
    go({ view: "chat", agentId }, { pane: `new-${newChatCount.current}` });
    setThreadsFrom(null);
    setMobileChatsOpen(false);
  };

  // Fork a chat: full copy of the transcript in a new chat, original untouched.
  const branchChat = (agentId: string, chatId: string) =>
    branchThread.mutate(chatId, {
      onSuccess: (branch) => {
        setActiveChat(agentId, branch.id);
        toast.success(`Branched from 「${branch.branched_from_title}」`, {
          description: "Continue here — the original chat stays untouched.",
        });
      },
    });

  const renameChat = (_agentId: string, chatId: string, title: string) => updateThread.mutate({ id: chatId, title });

  const togglePinChat = (agentId: string, chatId: string) => {
    const pinned = !anyAgent(agentId)?.chats.find((c) => c.id === chatId)?.pinned;
    updateThread.mutate(
      { id: chatId, pinned },
      { onSuccess: () => toast.success(pinned ? "Chat pinned to top" : "Chat unpinned") },
    );
  };

  const archiveChat = (agentId: string, chatId: string) =>
    updateThread.mutate(
      { id: chatId, archived: true },
      {
        onSuccess: () =>
          toast.success("Chat archived", {
            action: { label: "Undo", onClick: () => setChatArchived(agentId, chatId, false) },
          }),
      },
    );

  const deleteChat = (agentId: string, chatId: string) =>
    deleteThread.mutate(chatId, {
      onSuccess: () => {
        if (lastSeen.current.chat[agentId]?.chatId === chatId) lastSeen.current.chat[agentId] = undefined;
        if (whereNow.current.chatId === chatId) go({ view: "chat", agentId }, { replace: true });
        toast.success("Chat deleted");
      },
    });

  const setChatArchived = (_agentId: string, chatId: string, archived: boolean) => updateThread.mutate({ id: chatId, archived });

  // Opening an archived chat restores it first so it reappears in the sidebar tree.
  const openArchivedChat = (agentId: string, chatId: string) => {
    setChatArchived(agentId, chatId, false);
    setActiveChat(agentId, chatId);
  };

  /* The sidebar — one prop wiring shared by the inline (desktop) and SideSheet
     (mobile/tablet) placements. `w` wraps navigation callbacks so the sheet
     closes on selection. */
  const sidebar = (w: NavWrap = (f) => f, inSheet = false) => (
    <LeftSidebar
      agents={agents}
      definable={definable ? { active: definableOpen && !workspaceView, onOpen: w(() => selectAgent(definable.id)) } : undefined}
      view={view}
      onNav={w(openView)}
      activeAgentId={activeAgentId}
      onSelectAgent={w(selectAgent)}
      onOpenLink={w((path: string) => go(placeOf(path)))}
      inSheet={inSheet}
      collapsed={inSheet ? false : leftCollapsed}
      onToggleCollapsed={() => setLeftCollapsed((v) => !v)}
    />
  );

  // Nothing stands in for the agent list while it loads: the boot gate's empty shell. Nor for
  // Definable, on an agent's address: it may be Definable's.
  if (agentsPending || (place.agentId && builderPending)) return <div className="h-screen w-screen bg-[var(--surface-sunk)]" />;

  return (
    <AgentAvatarState.Provider value={avatarStates}>
    <SettingsNavigation.Provider value={openSettings}>
    {/* Every model picker's "Add connection" / "Replace key": the Models & providers pane, which opens on Connections when that is what needs doing. */}
    <OpenConnections.Provider value={() => openSettings("models")}>
    <div className="App flex h-dvh w-screen overflow-hidden bg-[var(--surface-sunk)] text-[var(--text-primary)]">
      {isDesktop && sidebar()}
      {/* Mobile/tablet: the sidebar lives in a left sheet instead of the flow */}
      {!isDesktop && (
        <>
          <SideSheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            {sidebar(viaSheet, true)}
          </SideSheet>
        </>
      )}
      <div className="flex-1 min-w-0 flex flex-col">
      {/* The content card sits in a grey tray, framed by the same outline as the
          rest of the app. The tray's top strip carries the agent tabs; workspace
          pages and Builder have none on desktop, so their card runs the tray's full height.
          Below lg the strip also carries the menu button. */}
      <div className="flex-1 min-h-0 flex flex-col mx-2 mb-2 mt-2 p-1 rounded-[22px] bg-[var(--surface-2)] ring-1 ring-[var(--outline)]">
      {(!(workspaceView || definableOpen) || !isDesktop) && (
        <TopBar
          view={view}
          definable={definableOpen}
          onNav={openView}
          onAgentNav={(key) =>
            key === "chat" ? openChat(activeAgentId)
            : key === "tasks" ? openTasks(activeAgentId)
            : key === "interfaces" ? openInterfaces(activeAgentId)
            : key === "reports" ? openReports(activeAgentId)
            : key === "spaces" ? openSpaces(activeAgentId)
            : key === "settings" ? openAgentSettings(activeAgentId)
            : key === "traces" ? openTraces(activeAgentId)
            : key === "evals" ? openEvals(activeAgentId)
            : openTraces(activeAgentId)
          }
          onOpenMobileNav={() => setMobileNavOpen(true)}
        />
      )}
      {/* The content card. The chat view draws its own cards (the chat, and its
          Chats history beside it), so here it is only the row they sit in. */}
      <div className={view === "chat" ? "flex-1 min-h-0 flex" : "flex-1 min-h-0 flex rounded-[18px] overflow-hidden bg-[var(--canvas)] ring-1 ring-[var(--hairline)] shadow-lift"}>
        {view === "overview" ? (
          <OverviewPage agents={agents} onOpenTraces={openTraces} />
        ) : view === "workspace-settings" ? (
          <SettingsPage section={workspaceSection} onSectionChange={(section) => go({ view: "workspace-settings", section })} onSignOut={signOut} />
        ) : view === "apps" ? (
          <AppsPage />
        ) : view === "knowledge" ? (
          <KnowledgeView kbId={activeKbId} nodeId={activeKbNodeId} onOpen={openKb} />
        ) : view === "skills" ? (
          <SkillsPage onAskDefinable={definable ? askDefinable : undefined} onOpenAgent={openAgentSkills} />
        ) : view === "templates" ? (
          <TemplatesPage onUseTemplate={useTemplate} />
        ) : view === "schedule" ? (
          <SchedulePage
            agents={agents}
            onAddTask={addTask}
            onUpdateTask={updateTask}
            onDeleteTask={deleteTask}
          />
        ) : view === "archive" ? (
          <ArchivePage
            agents={agents}
            onRestoreChat={(agentId, chatId) => setChatArchived(agentId, chatId, false)}
            onOpenChat={openArchivedChat}
          />
        ) : view === "tasks" ? (
          <TasksPage
            agent={activeAgent!}
            agents={agents}
            onAddTask={(task) => addTask(activeAgentId, task)}
            onUpdateTask={(taskId, patch) => updateTask(activeAgentId, taskId, patch)}
            onDeleteTask={(taskId) => deleteTask(activeAgentId, taskId)}
            onRunTask={(task) => runTask(activeAgentId, task)}
            onOpenChat={(threadId) => setActiveChat(activeAgentId, threadId)}
          />
        ) : view === "interfaces" ? (
          <InterfacesPage
            key={activeAgentId}
            agent={activeAgent}
            onOpenTraces={() => openTraces(activeAgentId)}
            onSimulated={simulateIncoming}
            onViewChats={(iface) => openThreads(activeAgentId, iface)}
          />
        ) : view === "reports" ? (
          <ReportsPage
            key={activeAgentId}
            agent={activeAgent}
            onFixWithBuilder={(report) => fixWithBuilder(activeAgentId, report)}
          />
        ) : view === "spaces" ? (
          <SpacesPage
            key={activeAgentId}
            agent={activeAgent!}
            place={spacesPlace}
            onPlace={(spaces, how) => go({ view: "spaces", agentId: activeAgentId, ...spaces }, how)}
            onAskDefinable={definable ? askDefinable : undefined}
            onOpenRun={(runId) => openTraces(activeAgentId, runId)}
          />
        ) : view === "traces" ? (
          <TracesPage
            key={activeAgentId}
            agent={activeAgent!}
            runId={traceRunId}
            onRunChange={(runId, how) => go({ view: "traces", agentId: activeAgentId, runId }, how)}
          />
        ) : view === "evals" ? (
          // Ungated until plans are real: with no billing every org reads as free
          // (backend/modules/accounts/business/caller_settings.py), and a Pro gate would
          // lock it for all.
          <EvalsPage
            key={activeAgentId}
            agent={activeAgent!}
            onOpenTrace={(origin) => openTraces(activeAgentId, origin?.runId || null)}
          />
        ) : view === "settings" ? (
          <AgentSettingsPage
            occupiedAvatars={agents.filter((agent) => agent.id !== activeAgentId)}
            key={activeAgentId}
            agent={activeAgent}
            section={settingsSection}
            onSectionChange={(section) => go({ view: "settings", agentId: activeAgentId, section })}
            onUpdateAgent={(updater) => updateAgent(activeAgentId, updater)}
            onDuplicateAgent={() => duplicateAgent(activeAgentId)}
            onDeleteAgent={() => deleteAgent(activeAgentId)}
            onboarding={activeAgent && (
              <OnboardingSection agent={activeAgent} onOpenThread={(threadId) => setActiveChat(activeAgentId, threadId)} />
            )}
          />
        ) : (
          /* Every callback below fires from inside a rendered chat, so the
             chat id the shell is holding is set by the time it runs. */
          <ChatPane
            key={`${activeAgentId}:${pane}`}
            agent={activeAgent}
            historyOpen={chatsOpen}
            onHistoryOpenChange={setChatsOpen}
            history={
              <ChatHistory
                key={activeAgentId}
                agent={activeAgent}
                from={threadsFrom}
                onShowAll={() => openThreads(activeAgentId)}
                onClose={() => setChatsOpen(false)}
                activeChatId={activeChatId}
                onSelectChat={setActiveChat}
                onNewChat={createChat}
                onRenameChat={renameChat}
                onTogglePinChat={togglePinChat}
                onBranchChat={branchChat}
                onArchiveChat={archiveChat}
                onDeleteChat={deleteChat}
              />
            }
            chat={activeChat}
            configSummary={activeAgent && !activeAgent.definable && (
              <>
                <ConfigSummary agent={activeAgent} onOpen={() => openAgentSettings(activeAgentId)} />
                <ChatKnowledge agentId={activeAgentId} chatId={activeChatId} />
              </>
            )}
            composerNote={activeAgent && !activeAgent.definable && <UnpublishedNote agent={activeAgent} />}
            onChatStarted={(chatId) => rememberChat(activeAgentId, chatId, pane)}
            startWith={activeAgent?.definable ? builderStart : null}
            onStartedWith={() => setBuilderStart(null)}
            onNewAgent={definable ? newAgentWithBuilder : undefined}
            onRenameChat={(title) => renameChat(activeAgentId, activeChatId!, title)}
            onTogglePinChat={() => togglePinChat(activeAgentId, activeChatId!)}
            onBranchChat={() => branchChat(activeAgentId, activeChatId!)}
            onOpenChat={(chatId) =>
              activeAgent?.chats.some((c) => c.id === chatId)
                ? setActiveChat(activeAgentId, chatId)
                : toast.error("Original chat no longer exists")
            }
            onArchiveChat={() => archiveChat(activeAgentId, activeChatId!)}
            onDeleteChat={() => deleteChat(activeAgentId, activeChatId!)}
          />
        )}
      </div>
      </div>
      </div>
    </div>
    </OpenConnections.Provider>
    </SettingsNavigation.Provider>
    </AgentAvatarState.Provider>
  );
}

/* Boot gate. Which screen you land on is the server's answer, not the browser's
   memory: `org` null means there is no workspace yet, an open `onboarding` means
   the wizard resumes where it stopped, and anything else is the workspace.
   Nothing renders until that answer is in — a shell, not a flash of the wrong
   person's name — which is what lets the deep tree read `settings` without null
   checks.

   The wizard ends having created the first agent; `setup` carries its id into
   Workspace so setup lands in that agent's chat. */
function Entry() {
  const { data: settings, isPending, error, refetch } = useSettings();
  useEvents(); // one socket per tab: other devices' changes show up here
  const [setup, setSetup] = useState<{ agentId: string | null } | null>(null);
  const navigate = useNavigate();
  // Setup ends inside the agent it just built, not on a dashboard.
  const finishSetup = (done: { agentId: string | null }) => {
    if (done.agentId) navigate(pathOf({ view: "chat", agentId: done.agentId }), { replace: true });
    setSetup(done);
  };

  /* Points the avatar store at this account: the saved look is per-user browser
     state, so it can only be read once the account is known. In render, not an
     effect — an effect runs after the tree below has already painted, and the
     sidebar would draw the default face for a frame. Idempotent, and the first
     call happens before anything is subscribed. */
  if (settings) loadUserAvatar(settings.user.id);
  // the same for unsent drafts: another account on this tab reads none of them
  if (settings) draftsFor(settings.user.id);

  // The wizard just finished: its result is the one thing the refetched
  // settings body cannot carry, so this branch comes first.
  if (setup) return <Workspace />;
  if (isPending) return <div className="h-screen w-screen bg-[var(--surface-sunk)]" />;
  if (error || !settings) {
    /* This query is `meta.silent` — no global toast — so the card has to say
       everything. "Couldn't reach the server" is only honest when the request
       never got an answer (status 0); anything else has a real message from the
       backend, and a request_id that turns a support ticket into a trace. */
    const api = error instanceof ApiError ? error : null;
    return (
      <div className="h-screen w-screen bg-[var(--surface-sunk)] flex items-center justify-center p-6">
        <div className="w-[360px] max-w-full rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] p-4 fade-in">
          <div className="text-[14px] font-medium tracking-[-0.005em]">Couldn't load your workspace</div>
          <div className="text-[12px] text-[var(--text-tertiary)] mt-1 leading-[1.55]">
            {api && api.status !== 0
              ? api.message
              : "We couldn't reach the server. Check your connection and try again."}
          </div>
          {api?.request_id && (
            <div className="mt-2 font-mono tabular-nums text-[11px] text-[var(--text-quaternary)]">
              ref {api.request_id}
            </div>
          )}
          <button
            onClick={() => refetch()}
            className="press mt-4 h-8 px-3.5 rounded-[10px] bg-[var(--brand)] shadow-button text-[var(--brand-foreground)] text-[12px] font-medium hover:bg-[var(--brand-hover)] flex items-center gap-1.5"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
  if (!settings.org) return <CreateOrg />;
  if (!settings.onboarding.completed_at)
    return (
      <Onboarding
        initial={settings.onboarding}
        defaultOrgName={settings.org.name}
        onDone={finishSetup}
      />
    );
  return <Workspace />;
}

function App() {
  return (
    <>
    {/* One toaster for every screen, mounted before any of them: a toast fired
        while the workspace is still loading its agents must not be lost. */}
    <Toaster position="bottom-right" />
    <BrowserRouter>
      <OldHashLinks />
      <Routes>
        {/* The workspace. `RequireAuth` renders nothing until the SDK has read
            its session, then either the app or a bounce to /login. */}
        <Route
          path="/*"
          element={
            <RequireAuth>
              <Entry />
            </RequireAuth>
          }
        />
        {/* Outside the guard and outside the workspace chrome. */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        {/* An MCP server's sign-in lands here in a popup; it hands the code to
            the dialog that opened it and closes (features/agents). */}
        <Route path="/oauth/mcp/callback" element={<McpOAuthCallback />} />
        {/* A shared chat: read by anyone with the link, no account needed. */}
        <Route path="/share/:token" element={<SharedChatPage />} />
        {/* An agent's Chrome extension link: adds it to the reader's Definable extension. */}
        <Route path="/extension/:agentId" element={<ExtensionLinkPage />} />
      </Routes>
    </BrowserRouter>
    {/* Ceiling: dev-only annotation overlay; talks to `npx agentation-mcp server` on :4747 */}
    {process.env.NODE_ENV === "development" && <Agentation />}
    </>
  );
}

export default App;
